import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import DOMPurify from 'dompurify';
import { DefaultDataService } from 'app/shared/services/default-data.service';
@Component({
  selector: 'html-editor',
  templateUrl: './html-editor.component.html',
  styleUrls: ['./html-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class HtmlEditorComponent implements OnInit, OnDestroy, AfterViewInit {
  _readOnly: boolean;
  _value = '';
  _variables: string[];
  fontSizes = ['8pt', '10pt', '12pt', '14pt', '18pt', '24pt', '36pt'];
  items: any = [
    { name: 'undo' },
    { name: 'redo' },
    { name: 'separator' },
    {
      name: 'font',
      acceptedValues: [
        'Arial',
        'Courier New',
        'Georgia',
        'Impact',
        'Lucida Console',
        'Tahoma',
        'Times New Roman',
        'Verdana',
      ],
      options: { width: 120 },
    },
    {
      name: 'size',
      acceptedValues: ['8pt', '10pt', '12pt', '14pt', '18pt', '24pt', '36pt'],
      options: { width: 80, value: this.fontSizes[this.defaultDataService.defaultFontSize - 1] },
    },
    { name: 'separator' },
    { name: 'bold' },
    { name: 'italic' },
    { name: 'strike' },
    { name: 'underline' },
    { name: 'separator' },
    { name: 'alignLeft' },
    { name: 'alignCenter' },
    { name: 'alignRight' },
    { name: 'alignJustify' },
    { name: 'separator' },
    { name: 'bulletList' },
    { name: 'orderedList' },
    { name: 'separator' },
    {
      name: 'header',
      acceptedValues: [false, 1, 2, 3, 4, 5],
      options: { width: 120 },
    },
    { name: 'color' },
    { name: 'background' },
    { name: 'separator' },
    { name: 'link' },
    { name: 'image' },
    { name: 'separator' },
    { name: 'clear' },
  ];

  @Input() set value(val: string) {
    this._value = DOMPurify.sanitize(val);
  }
  @Input() placeholder = '';

  @Input() set variables(val: string[]) {
    this.items.splice(2, 0, {
      name: 'variable',
      options: { hint: 'Insert Field' },
    });
    this._variables = val;
  }

  @Input() set readOnly(val: boolean) {
    this._readOnly = val;
    this.updateState();
  }

  @Output() change: EventEmitter<string> = new EventEmitter<string>();

  debouncer: Subject<any> = new Subject();

  constructor(private defaultDataService: DefaultDataService) {}

  ngOnInit() {
    this.debouncer
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe((val) => {
        this.change.emit(DOMPurify.sanitize(val));
      });
  }

  ngOnDestroy(): void {
    if (this.debouncer) this.debouncer.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.updateState();

    const elements = document.getElementsByClassName(
      'ql-editor dx-htmleditor-content'
    );

    if (elements.length > 0){
      Array.from(elements).forEach(element => {
        const x = <HTMLElement>element;
      
        // if innerHTML is the equivalent of blank then replace with custom html for new note
        if (x.innerHTML === '<p><br></p>'){
          x.innerHTML = `<p><span style="font-size: ${this.fontSizes[this.defaultDataService.defaultFontSize - 1]};"><br></span></p>`;
        }
      });
    }
  }

  onValueChange(value) {
    this.debouncer.next(value.value);
  }

  updateState() {
    // toggle all toolbar items disabled state with the exception of 'strike' which should be always enabled
    this.items.forEach((item) => {
      switch (item.name) {
        case 'undo':
        case 'redo':
        case 'strike':
          break;
        default:
          item.disabled = this._readOnly;
          break;
      }
    });

    if (this._readOnly) {
      //  suppress keyboard events
      const element = document.getElementsByClassName('dx-quill-container');
      if (element && element.length > 0) {
        element[0].addEventListener('keydown', (e: any) => {
          const ctrlDown = e.ctrlKey || e.metaKey;
          if (ctrlDown) {
            switch (e.keyCode) {
              case 65: // ctrl + a
              case 67: // ctrl + c
                return true;
              default:
                break;
            }
          }
          e.preventDefault();
          return false;
        });
      }
    }
  }

  customizeQuillModules(config) {
    var bindings = {
      backspace: {
        key: 'backspace',
        handler: function () {
          // do nothing
        },
      },
      tab: {
        key: 9,
        handler: function () {
          // do nothing
        },
      },
      enter: {
        key: 'enter',
        handler: function () {
          // do nothing
        },
      },
      del: {
        key: 'del',
        handler: function () {
          // do nothing
        },
      },
    };
    config.keyboard = {
      bindings: bindings,
    };
  }
}
